'use client';
import { getUserCookies } from '@/service/action/user-cookies';
import { requestCancelOrder, requestGetMyOrder } from '@/lib/api/order';
import { useEffect, useState } from 'react';
import PageTitle from '@/components/center/PageTitle';
import {
  Button,
  DatePicker,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
} from 'antd';
import {
  MyOrderResponseProduct,
  MyOrderResponseType,
} from '@/lib/type/myOrder';
import { Placeholder } from '@/components/placeholder';
import Loading from '@/components/loading';
import { useCheckoutStore } from '@/lib/store/checkout';
import { sleep } from '@/lib/util/util';
import { router } from 'next/client';
import { getPayLink } from '@/lib/util/get-router';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { useOrderStatusStore } from '@/lib/store/orderstatus';

const OrderTable = () => {
  const { RangePicker } = DatePicker;
  const { Search } = Input;
  const [messageApi, contextHolder] = message.useMessage();
  const { orderStatusList, active, setActive,orderStatusMenu} = useOrderStatusStore();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<MyOrderResponseType[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  // 默认用接口返回的 defaultPageSize=20（避免分页数量和服务端不一致）
  const [pageSize, setPageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  // 下面这些状态用于“日期/订单号搜索”：
  // 由于后端目前只按分页返回“当前页”，因此为了让搜索覆盖全部分页，这里会在搜索时拉取“当前订单状态”的全部订单到前端，再进行筛选并分页展示。
  const [allOrders, setAllOrders] = useState<MyOrderResponseType[]>([]);
  const [allOrdersStatus, setAllOrdersStatus] = useState<string>(active);
  const [isFiltered, setIsFiltered] = useState(false);
  const [filteredOrders, setFilteredOrders] = useState<MyOrderResponseType[]>([]);
  const [orderSnQuery, setOrderSnQuery] = useState('');
  const [dateQuery, setDateQuery] = useState<{ start: number; end: number } | null>(null);
  // 获取订单数据
  const getOrderAction = async (
    page = currentPage,
    limit = pageSize,
    orderStatusOverride: string = active
  ) => {
    const userInfo = await getUserCookies();
    if (!userInfo) return;
    setLoading(true);
    const res = await requestGetMyOrder({
      'access-token': userInfo.access_token,
      page: String(page),
      limit: String(limit),
      order_status: orderStatusOverride,
    });
    if (res.code === 200) {
      const orders = res.data.oders;
      setData(orders);
      setDataSource(getDataByStatus(orders, orderStatusOverride));
      setTotalCount(Number(res.data.page?.totalCount ?? 0));
    }
    setLoading(false);
  };
  useEffect(() => {
    getOrderAction();
  }, []);

  // 时间转换函数
  const getTimeAction = (time: any) => {
    const date = new Date(time * 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // 月份从0开始，需要加1
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // 根据当前订单状态(active)过滤订单列表
  const getDataByStatus = (list: MyOrderResponseType[], status: string = active) => {
    if (status === '999') return list;
    return list.filter((item) => item.order_status === status);
  };

  const FETCH_ALL_LIMIT = 20;

  const fetchAllOrdersByStatus = async (orderStatus: string) => {
    const userInfo = await getUserCookies();
    if (!userInfo) return [];

    const acc: MyOrderResponseType[] = [];
    let page = 1;
    let total = 0;

    // 依赖接口返回的 totalCount，循环拉取直到拿满
    while (true) {
      const res = await requestGetMyOrder({
        'access-token': userInfo.access_token,
        page: String(page),
        limit: String(FETCH_ALL_LIMIT),
        order_status: orderStatus,
      });

      if (res.code !== 200) break;

      const orders = res.data.oders ?? [];
      acc.push(...orders);
      total = Number(res.data.page?.totalCount ?? acc.length);

      if (acc.length >= total || orders.length === 0) break;
      page += 1;
    }

    return acc;
  };

  const ensureAllOrdersLoaded = async (orderStatus: string) => {
    if (allOrdersStatus === orderStatus && allOrders.length > 0) return allOrders;
    const orders = await fetchAllOrdersByStatus(orderStatus);
    setAllOrders(orders);
    setAllOrdersStatus(orderStatus);
    return orders;
  };

  const applyFiltersAndUpdatePagination = (
    page: number,
    nextPageSize: number,
    orderStatus: string,
    sn: string,
    range: { start: number; end: number } | null,
    sourceOrders?: MyOrderResponseType[]
  ) => {
    const base = sourceOrders ?? allOrders;

    let filtered = getDataByStatus(base, orderStatus);

    if (range) {
      filtered = filtered.filter((item) => {
        const t = parseInt(String(item.created_at));
        return t >= range.start && t <= range.end;
      });
    }

    if (sn) {
      filtered = filtered.filter((item) => item.order_sn.includes(sn));
    }

    setFilteredOrders(filtered);
    setTotalCount(filtered.length);
    setIsFiltered(!!range || !!sn);

    const startIndex = (page - 1) * nextPageSize;
    const pageData = filtered.slice(startIndex, startIndex + nextPageSize);
    setDataSource(pageData);
  };

  // 日期选择器
  const onRangePickerChange = async (dates: any[], dateStrings: [string, string]) => {
    setLoading(true);

    if (!dateStrings[0]) {
      const nextRange = null;
      setDateQuery(nextRange);

      // 如果订单号也没有，就回到服务端分页
      if (!orderSnQuery) {
        setIsFiltered(false);
        setFilteredOrders([]);
        await getOrderAction(currentPage, pageSize);
        setLoading(false);
        return;
      }

      // 否则只保留订单号搜索（覆盖全部分页）
      const orders = await ensureAllOrdersLoaded(active);
      applyFiltersAndUpdatePagination(1, pageSize, active, orderSnQuery, nextRange, orders);
      setCurrentPage(1);
      setLoading(false);
      return;
    }

    const start = new Date(dateStrings[0]).getTime();
    const end = new Date(dateStrings[1]).getTime();
    const nextRange = { start, end };
    setDateQuery(nextRange);

    const orders = await ensureAllOrdersLoaded(active);
    applyFiltersAndUpdatePagination(1, pageSize, active, orderSnQuery, nextRange, orders);
    setCurrentPage(1);
    setLoading(false);
  };
  // 订单转态选择器
  const changeStatusAction = async (str: string) => {
    setLoading(true);
    setActive(str);
    setCurrentPage(1);

    // 如果没有任何搜索条件，直接请求服务端该状态的第一页
    if (!orderSnQuery && !dateQuery) {
      setIsFiltered(false);
      setFilteredOrders([]);
      await getOrderAction(1, pageSize, str);
      setLoading(false);
      return;
    }

    // 如果存在搜索条件，则保持筛选条件不变，但要覆盖“全部分页”
    const orders = await ensureAllOrdersLoaded(str);
    applyFiltersAndUpdatePagination(1, pageSize, str, orderSnQuery, dateQuery, orders);
    setLoading(false);
  };



  // 可以取消订单的订单状态
  const canCancel = (order_status: string) => {
    const list = [
      {
        order_status: '0',
        name: '等待买家支付',
      },
      {
        order_status: '10',
        name: '等待卖家发货',
      },
      {
        order_status: '19',
        name: '部分商品已发货',
      },
      {
        order_status: '20',
        name: '等待买家确认',
      },
    ];
    const res = list.find((item) => item.order_status === order_status);
    return !!res;
  };

  const getOrderTypeStatus = (str: string) => {
    return orderStatusMenu[str] ?? str;
  };
  const getOrderPayStatus = (str: string) => {
    return orderStatusMenu[str] ?? str;
  };


  // 表格源数据
  const [dataSource, setDataSource] = useState<MyOrderResponseType[]>([]);

  // 搜索框
  const onSearch = async (value: string) => {
    const nextSn = value?.trim() ?? '';
    setLoading(true);
    setOrderSnQuery(nextSn);

    // 都清空则回到服务端分页
    if (!nextSn && !dateQuery) {
      setIsFiltered(false);
      setFilteredOrders([]);
      await getOrderAction(currentPage, pageSize);
      setLoading(false);
      return;
    }

    // 否则使用“当前状态的全部订单”进行前端筛选 + 分页展示
    const orders = await ensureAllOrdersLoaded(active);
    applyFiltersAndUpdatePagination(1, pageSize, active, nextSn, dateQuery, orders);
    setCurrentPage(1);
    setLoading(false);
  };

  // 通知弹窗
  const key = 'updatable';
  const messageLoading = () => {
    messageApi.open({
      key,
      type: 'loading',
      content: '取消中',
    });
  };
  const messageSuccess = () => {
    messageApi.open({
      key,
      type: 'success',
      content: '订单取消成功',
      duration: 2,
    });
  };

  // 取消订单
  const cancelOrderAction = async (id: string) => {
    const userInfo = await getUserCookies();
    messageLoading();
    if (!userInfo) return;
    setLoading(true);
    const res = await requestCancelOrder({
      'access-token': userInfo.access_token,
      id,
    });

    // 取消后刷新当前视图（服务端分页或前端筛选模式）
    if (isFiltered) {
      const orders = await ensureAllOrdersLoaded(active);
      applyFiltersAndUpdatePagination(1, pageSize, active, orderSnQuery, dateQuery, orders);
      setCurrentPage(1);
    } else {
      await getOrderAction(currentPage, pageSize, active);
    }
    setLoading(false);
    messageSuccess();
  };

  const { setOrderId } = useCheckoutStore();
  const router = useRouter();
  const goPay = async (id: string) => {
    setOrderId(id);
    await sleep(500);
    router.push(getPayLink());
  };

  // 确认取消订单
  const ConfirmCancelAction = (id: string) => {
    cancelOrderAction(id);
  };
  const columns = [
    {
      title: '商品',
      render: (text: MyOrderResponseProduct[], record: MyOrderResponseType) => (
        <div className="s-flex !items-start">
          <Placeholder
            src={record.product[0].product_picture}
            imageWidth={80}
            imageHeight={80}
          ></Placeholder>
          <div className="ml-2">
            <p className="max-w-[200px] line-clamp-2">
              {record.product[0].product_name}
            </p>
          </div>
        </div>
      ),
    },
    {
      title: '数量',
      render: (text: MyOrderResponseProduct[], record: MyOrderResponseType) => (
        <span>{record.product[0].num}</span>
      ),
    },
    {
      title: '订单金额（元）',
      render: (text: MyOrderResponseProduct[], record: MyOrderResponseType) => (
        <span>{record.product[0].price}</span>
      ),
    },
    {
      title: '订单状态',
      render: (text: MyOrderResponseProduct[], record: MyOrderResponseType) => (
        <span>{getOrderTypeStatus(record.order_status)}</span>
      ),
    },
    {
      title: '交易操作',
      render: (text: MyOrderResponseProduct[], record: MyOrderResponseType) => (
        <>
          {record.order_status === '0' ? (
            <span
              onClick={() => goPay(record.id)}
              className={' hover:text-main duration-300 cursor-pointer'}
            >
              {getOrderPayStatus(record.order_status)}
            </span>
          ) : (
            <span>{getOrderPayStatus(record.order_status)}</span>
          )}
        </>
      ),
    },
    {
      title: '交易状态',
      render: (text: MyOrderResponseProduct[], record: MyOrderResponseType) => (
        <>
          {/*<span className='cursor-pointer' onClick={showModal}>*/}
          {/*    取消订单*/}
          {/*</span>*/}
          {/*<Modal title="Basic Modal" open={isModalOpen} onOk={handleOk} styles={{ mask: { backgroundColor: 'rgba(0, 0, 0, 0.5)' } }} keyboard={true} onCancel={handleCancel}>*/}
          {/*    <p>Some contents...</p>*/}
          {/*    <p>Some contents...</p>*/}
          {/*    <p>Some contents...</p>*/}
          {/*</Modal>*/}
          <Popconfirm
            title="确认取消该订单吗"
            okText="确认"
            cancelText="取消"
            onConfirm={() => ConfirmCancelAction(record.id)}
          >
            {canCancel(record.order_status) && (
              <span className="cursor-pointer hover:text-main duration-300">取消订单</span>
            )}
          </Popconfirm>
        </>
      ),
    },
  ];

  const components = {
    body: {
      // 自定义行
      row: (props: any) => {
        const rowData = dataSource.find(
          (item) => item.id === props['data-row-key']
        );
        return (
          <>
            <tr style={{ height: '20px', backgroundColor: 'transparent' }}></tr>
            {rowData && (
              <tr style={{ backgroundColor: '#fef3f0' }}>
                <td colSpan={2}>
                  <span className="text-[#a09f9f]">订单号：</span>
                  {rowData.order_sn}
                </td>
                <td colSpan={2}>
                  <span className="text-[#a09f9f]">下单时间：</span>
                  {getTimeAction(rowData.created_at)}
                </td>
                <td colSpan={2}>
                  <span className="text-[#a09f9f]">预计发货：</span>
                </td>
              </tr>
            )}
            <tr className="custom-row shadow-md !mb-10">{props.children}</tr>
          </>
        );
      },
    },
  };

  return (
    <>
      <div className={'pb-6 b-flex py-6'}>
        <div className="s-flex">
          <span className="mr-2">订单状态</span>
          <Select
            style={{ width: 120, border: '1px solid #ccc' }}
            onChange={(value) => changeStatusAction(String(value))}
            value={active}
            options={orderStatusList}
          />
        </div>

        <div className="s-flex">
          <span>下单日期：</span>
          <Space direction="vertical" size={12}>
          {/* @ts-ignore */}
            <RangePicker onChange={onRangePickerChange} />
          </Space>
        </div>

        <div className="s-flex">
          <p className=" flex-shrink-0 mr-2">订单号</p>
          <Search
            placeholder=""
            allowClear
            enterButton="搜索"
            size="large"
            onSearch={onSearch}
          />
        </div>
      </div>

      {contextHolder}

      {loading ? (
        <Loading />
      ) : (
        <Table
          dataSource={dataSource}
          columns={columns}
          rowClassName={'!mb-10'}
          rowKey={'id'}
          components={components}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalCount,
            showSizeChanger: false,
            onChange: (page, newPageSize) => {
              setCurrentPage(page);
              setPageSize(newPageSize);
              if (isFiltered) {
                const startIndex = (page - 1) * newPageSize;
                setDataSource(filteredOrders.slice(startIndex, startIndex + newPageSize));
              } else {
                getOrderAction(page, newPageSize);
              }
            },
          }}
        />
      )}
    </>
  );
};

export const Order = () => {
  return (
    <>
      <PageTitle title="我的订单" />
      <OrderTable />
    </>
  );
};
